import h from 'hyperscript';

import errorIcon from '@disy/cadenza-icons/error.svg';
import plusIcon from '@disy/cadenza-icons/plus.svg';
import warningIcon from '@disy/cadenza-icons/warning.svg';
import type { StyleClass } from '@disy/lib/add-style-class';
import { addStyleClass } from '@disy/lib/add-style-class';
import { assertNonNullable } from '@disy/lib/custom-error';
import { icon } from '@disy/lib/icon';
import { setupScrollShadow } from '@disy/lib/scroll-shadow';
import { cadenzaSessionStorage } from '@disy/lib/storage';
import { createButton, createIconButton } from '@disy/ui/button';
import type { Disclosure } from '@disy/ui/disclosure';
import { setupMenuButton } from '@disy/ui/menu';
import { createMoreActionsButton } from '@disy/ui/more-actions-button';
import { setupPopupButton } from '@disy/ui/popup';

import { createEmptyListContent } from 'ui/attribute-list/attribute-list-utils';
import type { FurtherActionsProvider } from 'ui/attribute-list/create-attribute-list';
import { createAttributeListGroupedByCategory } from 'ui/attribute-list/create-attribute-list';
import { createObjecttypeSummary } from 'ui/resource-summary/objecttype-summary';
import type { Select } from 'ui/select/select';
import { createSelectWithSearchButton } from 'ui/select/select-helper';

import { hasPrivilege } from 'cadenza/access-management/model/object-privilege-util';
import type { ObjecttypeWithAttributes } from 'cadenza/api-client/dto/objecttype-dto';
import { isFeatureAvailable } from 'cadenza/features';
import { AttributeCategory } from 'cadenza/repository/attribute-category';
import { objecttypeIdFromString } from 'cadenza/repository/model/objecttype-id.gen';
import { repositoryService } from 'cadenza/repository/repository-service';
import { getStore } from 'cadenza/store/cadenza-store';
import { setupDataTransfer } from 'cadenza/utils/datatransfer';
import { markTokensInElement } from 'cadenza/utils/mark-substring';
import { type Objecttype, isObjecttypeBoundManageable } from 'cadenza/workbook/api/objecttype';
import type { WorkbookAttributeDto } from 'cadenza/workbook/api/workbook-attribute-dto';
import { isEnrichmentOutdatedAndRerunnable } from 'cadenza/workbook/enrichment/enrichment-selectors.state';
import type { EnrichmentDto } from 'cadenza/workbook/enrichment/model/enrichment-dto';
import { SortableList } from 'cadenza/workbook/sortable-list/sortable-list';
import { filterObjecttypes, getGroupedEnrichmentsFromObjecttype } from 'cadenza/workbook/workbook-attribute-util';
import {
  canManageObjecttype,
  getAllEnrichmentsFromObjecttype,
  getAttributeById,
  getDataSourceByObjecttypeId,
  getObjecttypeById,
  getVisibleObjecttypes,
  isWorkbookVisible,
  resolveObjecttypeAttributes,
} from 'cadenza/workbook/workbook-core-selectors.state';
import { createEnrichmentActions } from 'cadenza/workbook/workbook-panel/databrowser/data-browser-enrichment-context-actions';
import i18n from 'cadenza/workbook/workbook-panel/databrowser/data-browser-item-list.properties';
import { createObjecttypeActions } from 'cadenza/workbook/workbook-panel/databrowser/data-browser-objecttype-context-actions';
import type { DataBrowserPanel } from 'cadenza/workbook/workbook-panel/databrowser/data-browser-panel';
import { createEnrichmentOutdatedAlert } from 'cadenza/workbook/workbook-panel/databrowser/enrichment-summary/enrichment-summary';
import { openAddDataDialog } from 'cadenza/workbook/workbook-panel/databrowser/open-add-data-dialog';
import { WorkbookPanelName } from 'cadenza/workbook/workbook-panel/workbook-panel-utils';

import './data-browser-item-list.css';

const MIN_SEARCH_CHARS = 2;
const DATA_BROWSER_OPEN_ITEMS = 'data-browser-open-items';

const COMPONENT_NAME = 'd-data-browser-item-list';
type EnrichmentAttributeFilter = (enrichmentAttribute: WorkbookAttributeDto) => boolean;

export class DataBrowserItemList extends HTMLElement {
  #objecttypes: ObjecttypeWithAttributes[] = [];
  #acceptedKeywords: string[] = [];
  #openSections = this.#loadOpenSections();

  readonly #header: HTMLElement;
  #select!: Select;
  #content?: HTMLElement;
  #addDataButton?: HTMLElement;

  #canAddData = false;
  #actionsForAttributeIdProvider;

  /**
   * @param [options] - Options
   * @param [options.actionsForAttributeIdProvider] - Returns a list of actions available for a given attribute (id)
   */
  constructor({
    actionsForAttributeIdProvider,
  }: {
    actionsForAttributeIdProvider?: FurtherActionsProvider;
    enrichmentAttributeFilter?: EnrichmentAttributeFilter;
  } = {}) {
    super();
    this.#actionsForAttributeIdProvider = actionsForAttributeIdProvider;
    this.#header = h(`.${COMPONENT_NAME}--header`);
  }

  connectedCallback() {
    this.classList.add(COMPONENT_NAME);
    this.append(this.#header);
    this.#createSelect();
    this.#openSections = this.#loadOpenSections();
    this.addEventListener('enrichment-details-toggle', () => this.#saveOpenSections());
  }

  /**
   * Rebuilds the entire data browser list
   *
   * @param objecttypes - The attribute groups to update the list to
   * @param canAddData - Whether the user can add data to the data browser
   */
  async updateList(objecttypes: Objecttype[] | ObjecttypeWithAttributes[], canAddData: boolean) {
    const tags = getTags(this.#objecttypes);
    this.#objecttypes = resolveObjecttypeAttributes(objecttypes);
    this.#canAddData = canAddData;
    this.#createSelect();
    this.#openSections = this.#loadOpenSections();
    const filteredObjecttypes = this.#getFilteredObjecttypes(tags);
    this.#updateContent(filteredObjecttypes);
  }

  #updateContent(objecttypes: ObjecttypeWithAttributes[]) {
    this.#clearPreviousContent();
    let content: HTMLElement;
    if (objecttypes.length) {
      this.#setHeaderHidden(false);
      content = this.#createList(objecttypes);
      if (this.#canAddData) {
        this.#addDataButton = createAddMoreDataButton();
      }
      content
        .querySelectorAll('d-disclosure')
        .forEach((section) => section.addEventListener('toggle', () => setupScrollShadow(this.#content!)));
    } else {
      const workbookHasData = getVisibleObjecttypes(getStore().getState()).length > 0;
      if (workbookHasData) {
        this.#setHeaderHidden(!this.#isFiltered);
        content = createWorkbookWithDataEmptyListContent(this.#isFiltered);
      } else {
        this.#setHeaderHidden(true);
        // show message that there is no data and if the user has no rights: that they cannot do anything about it
        content = createEmptyListContent();
        content.classList.add(`${COMPONENT_NAME}--empty-content`, 'd-stack-v', 'space-2');
      }
    }
    this.append((this.#content = content));
    if (this.#addDataButton) {
      // Normal, non-primary button at the bottom
      this.append(this.#addDataButton);
    }
  }

  #setHeaderHidden(hidden: boolean) {
    this.#select.hidden = hidden;
    this.#header.hidden = hidden;
  }

  #clearPreviousContent() {
    this.#content?.remove();
    this.#content = undefined;
    this.#addDataButton?.remove();
    this.#addDataButton = undefined;
  }

  #createSelect() {
    const header = this.#header;
    if (this.#select) {
      header.removeChild(this.#select);
    }
    const tags = getTags(this.#objecttypes);
    const updateFunction = () => {
      this.#updateContent(this.#getFilteredObjecttypes(tags));
    };
    this.#select = createSelectWithSearchButtonAndChangeListener(tags, updateFunction);
    header.append(this.#select);
  }

  #createList(objecttypes: ObjecttypeWithAttributes[]) {
    const sections: Disclosure[] = [];
    for (const objecttype of objecttypes) {
      if (!objecttype.attributes.length && !objecttype.enrichments?.length) {
        continue;
      }
      const allAttributes = this.#collectAllAttributes(objecttype);
      const isOpen = this.#isFiltered || this.#openSections.has(objecttype.id);
      const moreActionsButton = createMoreActionsButton(objecttype.printName, {
        size: 's',
        styleClass: 'objecttype-more-action',
      });

      const otErrorIcon = h('.ot-error-icon', [icon(errorIcon, { size: 's', variant: 'normal' })]);
      otErrorIcon.slot = 'summary-aside';

      moreActionsButton.slot = 'summary-aside';
      const withManagementLink = canManageObjecttype(objecttype.id);
      const enrichmentsForGrouping = getGroupedEnrichmentsFromObjecttype(objecttype.id);
      const enrichmentAttributeGroupings = enrichmentsForGrouping.map((enrichment) => ({
        enrichmentId: enrichment.objecttypeId,
        warningButtonFactory: () =>
          isEnrichmentOutdatedAndRerunnable(getStore().getState(), enrichment) ?
            createEnrichmentOutdatedWarningButton(enrichment)
          : undefined,
        moreActionsButtonFactory: () => createGroupedEnrichmentMoreActionsButton(enrichment),
      }));
      const attributeListGroupedByCategory = createAttributeListGroupedByCategory(allAttributes, {
        acceptedKeywords: this.#acceptedKeywords,
        hoverContext: true,
        showBoundToWorkbookHint: objecttype.boundState === 'UNBOUND',
        openEnrichmentSections: this.#openSections,
        withManagementLink,
        createAttributeList,
        enrichmentAttributeGroupings,
        getAttributeActions: this.#actionsForAttributeIdProvider,
        getAttributeById: (attrId) => getAttributeById(getStore().getState(), attrId),
      });
      const objecttypeSection = h<Disclosure>(
        'd-disclosure.d-workbook-section.d-hover-context.object-type',
        {
          expanded: isOpen,
          ontoggle: () => this.#saveOpenSections(),
          'data-attribute-group-id': objecttype.id,
        },
        [
          h('summary', markTokensInElement(h('span', [objecttype.printName, otErrorIcon]), this.#acceptedKeywords)),
          moreActionsButton,
          h('.d-stack-v.space-3', attributeListGroupedByCategory),
        ],
      );
      if (moreActionsButton) {
        setupMenuButton(
          objecttypeSection,
          async () => {
            const repositoryName = objecttypeIdFromString(objecttype.id).repositoryName;
            const canSeeWorkbook = isWorkbookVisible(getStore().getState());
            const canSeeObjecttype = objecttype.currentUserPrivileges.includes('visible');
            const canGoToManagementCenter =
              canSeeWorkbook &&
              canSeeObjecttype &&
              isFeatureAvailable('ADMINISTRATION') &&
              isObjecttypeBoundManageable(objecttype);
            let [repository, actions] = await Promise.all([
              repositoryService().get(repositoryName),
              createObjecttypeActions(objecttype.id),
            ]);
            const dataSource = getDataSourceByObjecttypeId(getStore().getState(), objecttype.id);
            const objecttypeSummary = createObjecttypeSummary({
              ...objecttype,
              repositoryPrintName: repository.printName,
              dataSourcePrintName: dataSource.printName,
              dataSourceType: dataSource.type,
              dataSourceFlavor: dataSource.flavor,
              withManagementLink: canGoToManagementCenter,
              withPublicationState: repository.publishingProcessEnabled && hasPrivilege(repository, 'modifyAll'),
            });

            return [objecttypeSummary, ...actions];
          },
          { styleClass: 'd-popup-info', delegate: '.objecttype-more-action' },
        );
      }
      sections.push(objecttypeSection);
    }
    if (sections.length === 1) {
      sections[0].expanded = true;
    }
    return h(`.${COMPONENT_NAME}--content.d-stack-v.space-4`, sections);
  }

  #getFilteredObjecttypes(tags: Tag[], objecttypes = this.#objecttypes) {
    const acceptedCategories: AttributeCategory[] = [];
    const acceptedKeywords: string[] = [];
    new Set(this.#select.selection).forEach((value) => {
      if (tags.some((tag) => tag.id === (value.id as AttributeCategory))) {
        acceptedCategories.push(value.id as AttributeCategory);
      } else if (value.id.length >= MIN_SEARCH_CHARS) {
        acceptedKeywords.push(value.id.trim().toLowerCase());
      }
    });
    this.#acceptedKeywords = acceptedKeywords;
    return filterObjecttypes(objecttypes, { acceptedCategories, acceptedKeywords });
  }

  #saveOpenSections() {
    // the state is not persisted when the details are open automatically during the search
    if (!this.#isFiltered) {
      const openSections = this.#openObjecttypeSections;
      this.#openSections = openSections;
      cadenzaSessionStorage.setItem(this.openSectionStorageKey, JSON.stringify(Array.from(openSections)));
    }
  }

  #loadOpenSections() {
    const item = cadenzaSessionStorage.getItem(this.openSectionStorageKey);
    return item ? new Set<string>(JSON.parse(item)) : new Set<string>();
  }

  get #openObjecttypeSections() {
    // the state is not persisted when the details are open automatically during the search
    assertNonNullable(this.#content, 'content net created yet');
    if (this.#content.matches('.empty-message')) {
      return new Set<string>();
    }
    return new Set(
      this.#queryAllObjecttypeSections()
        .filter((section) => section.expanded)
        .map((section) => section.dataset.attributeGroupId) as string[],
    );
  }

  #queryAllObjecttypeSections() {
    if (!this.#content) {
      return [];
    }
    return [...this.#content.querySelectorAll<Disclosure>('d-disclosure[data-attribute-group-id]')];
  }

  get openSectionStorageKey() {
    const source = this.closest<DataBrowserPanel>(WorkbookPanelName.DATA_BROWSER)?.selectedSource || 'flyout';
    return `${DATA_BROWSER_OPEN_ITEMS}-${source}`;
  }

  get #isFiltered() {
    return Boolean(this.#select.selection.length);
  }

  #collectAllAttributes(objecttype: ObjecttypeWithAttributes) {
    const allAttributes = [...objecttype.attributes].filter((attr) => attr.visibleInDataBrowser);
    const enrichments = getAllEnrichmentsFromObjecttype(getStore().getState(), objecttype.id);
    const allEnrichmentAttributes = enrichments
      .flatMap((enrichment) => {
        const enrichmentObjecttype = getObjecttypeById(getStore().getState(), enrichment.objecttypeId);
        const resolvedOt = resolveObjecttypeAttributes([enrichmentObjecttype]);
        const filteredObjectTypes = this.#getFilteredObjecttypes(getTags(resolvedOt), resolvedOt);
        return filteredObjectTypes[0]?.attributes ?? [];
      })
      .filter((attribute) => attribute.visibleInDataBrowser);
    allAttributes.push(...allEnrichmentAttributes);
    return allAttributes;
  }
}

customElements.define(COMPONENT_NAME, DataBrowserItemList);

function createGroupedEnrichmentMoreActionsButton(enrichment: EnrichmentDto) {
  const enrichmentPrintName = getObjecttypeById(getStore().getState(), enrichment.objecttypeId).printName;
  if (isFeatureAvailable('OBJECTTYPE_ENRICHMENT')) {
    const moreActionsButton = createMoreActionsButton(enrichmentPrintName, { size: 'xs' });
    setupMenuButton(moreActionsButton, () => createEnrichmentActions({ enrichment }));
    return moreActionsButton;
  }
}

function createEnrichmentOutdatedWarningButton(enrichment: EnrichmentDto) {
  const button = createIconButton(icon(warningIcon), '', {
    size: 'xs',
    variant: 'warning',
  });
  const content = () => createEnrichmentOutdatedAlert(enrichment);
  setupPopupButton(button, content, {
    arrow: true,
    placement: 'right-start',
    variant: ['info', 'padding-3'],
  });
  return button;
}

/**
 * Creates an attribute list by given list of attribute item elements and an unique labelId.
 */
function createAttributeList(
  attributeItems: Element[],
  labelId: string,
  { styleClass }: { styleClass?: StyleClass } = {},
) {
  const list = new SortableList({
    handleDropAreaActivation: true,
    draggableSelector: '.d-attribute-tile',
  });
  list.pull = 'copy';
  list.group = 'attributes-list';
  list.ariaLabelledBy = labelId;
  list.append(...attributeItems);
  setupDataTransfer(list, (event) => ({ attributeId: (event.target as HTMLElement).dataset.id }));
  addStyleClass(list, styleClass);
  return list;
}

function getTags(objecttypes: ObjecttypeWithAttributes[]) {
  return AttributeCategory.values
    .filter((category) =>
      objecttypes.some(({ attributes }) => attributes?.find((attribute) => attribute.category === category)),
    )
    .map((category) => ({ id: category, text: AttributeCategory.i18n(category, { count: 3 }) }));
}

function createAddMoreDataButton() {
  return createButton([icon(plusIcon), i18n('data-browser-list.add-more-data')], {
    styleClass: `${COMPONENT_NAME}--add-data`,
    onclick: () => openAddDataDialog(),
  });
}

function createWorkbookWithDataEmptyListContent(isFiltered: boolean) {
  return h(`.${COMPONENT_NAME}--empty-content`, [
    h(
      'd-alert',
      { type: 'info', size: 's' },
      isFiltered ?
        i18n('data-browser-list.empty.search')
      : i18n('data-browser-list.empty.no-suitable-attributes-found'),
    ),
  ]);
}

interface Tag {
  id: AttributeCategory;
  text: string;
}

function createSelectWithSearchButtonAndChangeListener(tags: Tag[], changeListener: () => void) {
  const select = createSelectWithSearchButton({
    data: tags,
    multiple: true,
    tags: true,
  });
  select.addEventListener('change', changeListener);
  return select;
}
